import { WORKSPACE_TABS, type WorkspaceTab } from '../../types/farmWorkspace';

/**
 * SINGLE source of Farm Workspace path building/parsing (redesign-plan §3.2, §9.6). Backend twin:
 * `WorkspaceRoutes` in `FarmWorkspaceDtos.cs`. Every deep link into the workspace goes through
 * these helpers — never string-concatenate a workspace URL elsewhere.
 *
 *   /farm-workspace                                          landing
 *   /farm-workspace/farmers/:farmerId[/:tab]                 farmer scope
 *   /farm-workspace/farmers/:farmerId/farms/:farmId[/:tab]   farm scope
 *      ?plot=:plotId                                         plot sub-filter
 */
export const BASE = '/farm-workspace';

export const DEFAULT_TAB: WorkspaceTab = 'overview';

/**
 * The menu id every hub route is registered under (`router.tsx` → `handle('entity-search')`, kept from
 * the v1 hub so role permissions carry over). Cross-links check `canAccessMenu(FARM_WORKSPACE_MENU_ID)`
 * so a viewer who cannot open the hub is never offered a button that lands on Access Denied.
 */
export const FARM_WORKSPACE_MENU_ID = 'entity-search';

/**
 * Superseded hub URLs that `router.tsx` redirects to `BASE` for the bookmark period: the v1
 * search-first hub (#384/#391) with EVERY sub-path (`entity-search/*` — farmers/farms/plots/…), and
 * the older `/agricultural-entities`. Relative (no leading slash) — they are children of the app shell.
 */
export const LEGACY_HUB_PATHS: readonly string[] = ['entity-search', 'entity-search/*', 'agricultural-entities'];

/** Query-string key carrying the plot sub-filter. */
export const PLOT_PARAM = 'plot';

export function isWorkspaceTab(raw: unknown): raw is WorkspaceTab {
  return typeof raw === 'string' && (WORKSPACE_TABS as readonly string[]).includes(raw);
}

/** Validate a `:tab` URL segment; anything unknown (or missing) falls back to `overview`. */
export function parseTab(raw: string | null | undefined): WorkspaceTab {
  return isWorkspaceTab(raw) ? raw : DEFAULT_TAB;
}

/** Positive-integer route/query param → number, else undefined. */
export function parseId(raw: string | null | undefined): number | undefined {
  if (raw === null || raw === undefined || !/^\d+$/.test(raw)) return undefined;
  const id = Number(raw);
  return id > 0 ? id : undefined;
}

const tabSegment = (tab?: WorkspaceTab): string => (tab && tab !== DEFAULT_TAB ? `/${tab}` : '');

/** Farmer scope (all farms). The default tab is omitted so the canonical URL stays short. */
export function farmerPath(farmerId: number, tab?: WorkspaceTab): string {
  return `${BASE}/farmers/${farmerId}${tabSegment(tab)}`;
}

/** Farm scope under its farmer. */
export function farmPath(farmerId: number, farmId: number, tab?: WorkspaceTab): string {
  return `${BASE}/farmers/${farmerId}/farms/${farmId}${tabSegment(tab)}`;
}

/**
 * Append (or clear) the `?plot=` sub-filter on a workspace path, preserving any other query
 * params already on it. Use it when switching tabs so the plot chip survives the navigation.
 */
export function withPlot(path: string, plotId?: number | null): string {
  const [pathname, query = ''] = path.split('?', 2);
  const params = new URLSearchParams(query);
  if (plotId) {
    params.set(PLOT_PARAM, String(plotId));
  } else {
    params.delete(PLOT_PARAM);
  }
  const qs = params.toString();
  return qs ? `${pathname}?${qs}` : pathname;
}

/** Read the plot sub-filter from the current search params. */
export function parsePlotParam(searchParams: URLSearchParams): number | undefined {
  return parseId(searchParams.get(PLOT_PARAM));
}
